import Focus from '@shared/types/enums/focus';
import Input from '../../atoms/text/dialInput';
import Modals from '@shared/types/enums/modals';
import Pages from '@shared/types/enums/pages';
import Recording from './dialRecording';
import goToZapperAction from '@shared/actions/channels/goToZapper';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { clearModal, modalSelector } from '@shared/store/slices/modal.slice';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { playerSelector } from '@shared/store/slices/player.slice';
import { recordingSelector } from '@shared/store/slices/recordings.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { setChannel } from '@shared/store/thunks/channels.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import { store } from '@shared/store/store';
import AnalyticsInstance from '@shared/utils/analytics';
import { OmnitrackPlaybackInteraction } from '@tvup/omnitrack-js';
import Channel from '@shared/classes/channel';

const DIAL_CHARACTER = '·';
const DIAL_DELAY = 1.5 * 1000;
const DIAL_LENGTH = 4;

export default function Dial() {
  const dispatch = useAppDispatch();

  const { pauseAd } = useAppSelector(adsSelector);
  const { channelsData, currentChannel } = useAppSelector(channelsSelector);
  const { focus } = useAppSelector(focusSelector);
  const { currentRecording } = useAppSelector(recordingSelector);
  const { current: currentModal } = useAppSelector(modalSelector);
  const { pilot } = useAppSelector(playerSelector);
  const { route } = useAppSelector(routerSelector);

  const [dial, setDial] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  const canDisplay = useRef(false);
  const canEdit = useRef(false);
  const pageRef = useRef(route.Page);

  canDisplay.current =
    focus !== Focus.ADS &&
    route.Page === Pages.ZAPPER &&
    !!(currentModal || pilot) &&
    currentModal !== Modals.PLAYER_LANGUAGE &&
    !pauseAd;
  canEdit.current =
    ![Pages.LANGUAGES, Pages.LINK, Pages.WELCOME].includes(route.Page) &&
    ![Focus.ADS, Focus.SPLASH].includes(focus as number);
  pageRef.current = route.Page;

  useEffect(() => {
    const currentEvent = currentChannel?.getCurrentEvent();
    if (currentEvent) {
      const { recordings } = store.getState();
      const found = recordings.identifiers.find(
        (item) => currentEvent.eventId === item.code
      );
      if (found) {
        setIsRecording(true);
        return;
      }
    }
    setIsRecording(false);
  }, [currentChannel?.id, currentRecording]);

  // Keyboard hooks

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      let number = '';
      switch (key) {
        case '0':
        case 'ZERO':
          number = '0';
          break;
        case '1':
        case 'ONE':
          number = '1';
          break;
        case 'TWO':
          number = '2';
          break;
        case 'THREE':
          number = '3';
          break;
        case 'FOUR':
          number = '4';
          break;
        case 'FIVE':
          number = '5';
          break;
        case 'SIX':
          number = '6';
          break;
        case 'SEVEN':
          number = '7';
          break;
        case 'EIGHT':
          number = '8';
          break;
        case 'NINE':
          number = '9';
          break;
      }
      if (canEdit.current && number) {
        setDial((dial) => {
          return dial.length < DIAL_LENGTH ? dial + number : dial;
        });
      }
    },
  });

  // Effect hooks

  useEffect(() => {
    const timeout = setTimeout(() => {
      const parsedDial = parseInt(dial);
      const foundDial = channelsData?.findIndex(
        (value: Channel) => value.dial === parsedDial
      );
      if (foundDial !== undefined && foundDial > -1) {
        AnalyticsInstance.setPlaybackInteraction(
          OmnitrackPlaybackInteraction.REMOTE_SHORTCUT
        );
        dispatch(setChannel(parsedDial));
        dispatch(clearModal());
        if (pageRef.current !== Pages.ZAPPER) {
          goToZapperAction();
        }
      }
      setDial('');
    }, DIAL_DELAY);
    return () => {
      clearTimeout(timeout);
    };
  }, [channelsData, dial]);

  return currentChannel ? (
    <>
      <Input showBackground={!!dial}>
        {(dial ? dial.padEnd(DIAL_LENGTH, DIAL_CHARACTER) : '') ||
          (canDisplay.current && route.Page === Pages.ZAPPER
            ? currentChannel?.dial
            : null)}
        {!dial &&
        canDisplay.current &&
        isRecording &&
        route.Page === Pages.ZAPPER ? (
          <Recording />
        ) : null}
      </Input>
    </>
  ) : null;
}
